// AUTO-CRM-32: ready-made pipelines for beginners. Each template becomes a real
// pipeline through the spreadsheet importer (POST /admin/pipelines/import):
// one row per step, linear connections, plus a starter lead table.

const won = (name, steps) => ({ name, probability: 100, closed: true, steps });
const lost = (name = 'Lost', steps = ['Note why it was lost']) => ({ name, probability: 0, closed: true, steps });
const starter = (key, label, extra = {}) => ({ key, label, type: key === 'phone' ? 'phone' : key === 'email' ? 'email' : 'text', starter: true, required: false, table: true, ...extra });
const field = (key, label, type, extra = {}) => ({ key, label, type, required: false, table: true, ...extra });
const choice = (key, label, options, extra = {}) => field(key, label, 'single_select', { options, ...extra });
const SOURCE = starter('source', 'Lead source');

export const PIPELINE_TEMPLATES = [
  {
    id: 'sales',
    name: 'Sales enquiries',
    icon: 'sales',
    tagline: 'For most businesses',
    description: 'Enquiries from your website, WhatsApp, calls and walk-ins, all the way to a paid order.',
    stages: [
      { name: 'New enquiry', probability: 10, steps: ['Call within 1 hour', 'Send a welcome message'] },
      { name: 'Contacted', probability: 25, steps: ['Understand what they need', 'Check budget and timeline'] },
      { name: 'Quote sent', probability: 50, steps: ['Send the quote', 'Follow up in 2 days'] },
      { name: 'Negotiation', probability: 75, steps: ['Answer questions and objections', 'Confirm the order'] },
      won('Won', ['Collect payment', 'Hand over to delivery']),
      lost(),
    ],
    sections: [
      { name: 'Contact', fields: [starter('phone', 'Phone', { required: true }), starter('email', 'Email'), starter('company', 'Company'), SOURCE] },
      { name: 'Requirement', fields: [field('requirement', 'What they need', 'long_text', { table: false }), field('deal_value', 'Deal value', 'currency'), choice('lead_temperature', 'Lead temperature', ['Hot', 'Warm', 'Cold']), choice('buying_timeline', 'When they want to buy', ['Right away', 'This month', 'In 1–3 months', 'Just exploring'])] },
    ],
  },
  {
    id: 'real_estate',
    name: 'Real estate',
    icon: 'home',
    tagline: 'Builders, brokers, rentals',
    description: 'From a property enquiry to a site visit and a booking.',
    stages: [
      { name: 'New enquiry', probability: 10, steps: ['Call and share project details', 'Send brochure on WhatsApp'] },
      { name: 'Site visit planned', probability: 30, steps: ['Confirm the visit a day before'] },
      { name: 'Site visit done', probability: 50, steps: ['Share price sheet and floor plan', 'Follow up within 2 days'] },
      { name: 'Negotiation', probability: 75, steps: ['Discuss price and payment plan', 'Check home loan eligibility'] },
      won('Booked', ['Collect booking amount', 'Share agreement']),
      lost(),
    ],
    sections: [
      { name: 'Contact', fields: [starter('phone', 'Phone', { required: true }), starter('email', 'Email'), SOURCE] },
      { name: 'Property need', fields: [field('budget', 'Budget', 'currency'), field('preferred_location', 'Preferred location', 'text'), choice('property_type', 'Property type', ['1 BHK', '2 BHK', '3 BHK', '4+ BHK', 'Villa', 'Plot', 'Commercial']), choice('purpose', 'Buying for', ['Own use', 'Investment']), field('needs_home_loan', 'Needs a home loan', 'checkbox', { table: false })] },
    ],
  },
  {
    id: 'education',
    name: 'Admissions',
    icon: 'education',
    tagline: 'Schools, colleges, coaching',
    description: 'Student and parent enquiries through counselling to a confirmed admission.',
    stages: [
      { name: 'Enquiry', probability: 10, steps: ['Call the student or parent', 'Share course details'] },
      { name: 'Counselling', probability: 30, steps: ['Hold a counselling session', 'Recommend a course'] },
      { name: 'Campus visit or demo', probability: 50, steps: ['Book a visit or demo class'] },
      { name: 'Application', probability: 75, steps: ['Help fill the application', 'Collect documents'] },
      won('Admission confirmed', ['Collect fees', 'Share joining details']),
      lost('Not joining'),
    ],
    sections: [
      { name: 'Contact', fields: [starter('phone', 'Phone', { required: true }), starter('email', 'Email'), SOURCE, field('parent_phone', 'Parent’s phone', 'phone', { table: false })] },
      { name: 'Course', fields: [field('course', 'Course interested in', 'text'), field('current_class', 'Current class or qualification', 'text'), choice('intake', 'Intake', ['This term', 'Next term', 'Next year']), field('city', 'City', 'text')] },
    ],
  },
  {
    id: 'agency',
    name: 'Agency & services',
    icon: 'agency',
    tagline: 'Marketing, IT, consulting',
    description: 'Discovery calls, proposals and retainers for service businesses.',
    stages: [
      { name: 'Lead in', probability: 10, steps: ['Research the company', 'Book a discovery call'] },
      { name: 'Discovery call', probability: 30, steps: ['Understand goals and budget', 'Identify the decision maker'] },
      { name: 'Proposal sent', probability: 55, steps: ['Send the proposal', 'Walk them through it'] },
      { name: 'Negotiation', probability: 75, steps: ['Agree scope and price'] },
      won('Won', ['Sign the contract', 'Kick-off call']),
      lost(),
    ],
    sections: [
      { name: 'Contact', fields: [starter('phone', 'Phone'), starter('email', 'Email', { required: true }), starter('company', 'Company'), starter('website', 'Website'), starter('designation', 'Designation'), SOURCE] },
      { name: 'Deal', fields: [field('services', 'Services needed', 'multi_select', { options: ['Website', 'SEO', 'Ads', 'Social media', 'Branding', 'Software'] }), field('monthly_budget', 'Monthly budget', 'currency'), field('deal_value', 'Deal value', 'currency')] },
    ],
  },
  {
    id: 'clinic',
    name: 'Clinic & wellness',
    icon: 'clinic',
    tagline: 'Clinics, salons, gyms',
    description: 'Enquiries to booked appointments and returning customers.',
    stages: [
      { name: 'Enquiry', probability: 10, steps: ['Call back and answer questions'] },
      { name: 'Appointment booked', probability: 40, steps: ['Send a reminder the day before'] },
      { name: 'Visited', probability: 60, steps: ['Share the treatment or plan', 'Follow up in 3 days'] },
      won('Started', ['Collect payment', 'Book the next visit']),
      lost('Not proceeding'),
    ],
    sections: [
      { name: 'Contact', fields: [starter('phone', 'Phone', { required: true }), starter('email', 'Email'), SOURCE] },
      { name: 'Visit', fields: [field('service', 'Service or treatment', 'text'), field('preferred_date', 'Preferred date', 'date'), field('age', 'Age', 'number', { table: false }), field('notes', 'Notes', 'long_text', { table: false })] },
    ],
  },
  {
    id: 'b2b',
    name: 'B2B & distribution',
    icon: 'b2b',
    tagline: 'Manufacturers, traders, IndiaMART',
    description: 'Bulk enquiries, samples and quotations through to a confirmed order.',
    stages: [
      { name: 'Enquiry', probability: 10, steps: ['Call to confirm the requirement'] },
      { name: 'Requirement confirmed', probability: 30, steps: ['Note quantity and specifications'] },
      { name: 'Quotation sent', probability: 50, steps: ['Send quotation', 'Send samples if needed'] },
      { name: 'Price negotiation', probability: 75, steps: ['Agree price and payment terms'] },
      won('Order confirmed', ['Share proforma invoice', 'Plan dispatch']),
      lost(),
    ],
    sections: [
      { name: 'Contact', fields: [starter('phone', 'Phone', { required: true }), starter('email', 'Email'), starter('company', 'Company'), starter('company_location', 'City'), SOURCE] },
      { name: 'Requirement', fields: [field('product', 'Product', 'text'), field('quantity', 'Quantity', 'number'), field('order_value', 'Order value', 'currency'), field('gst_number', 'GST number', 'text', { table: false })] },
    ],
  },
];

const COLUMNS = { stage_name: 'Stage', stage_order: 'Stage order', stage_probability: 'Probability', stage_is_closed: 'Closed', node_name: 'Step', node_type: 'Step type' };

// Custom field keys are unique per workspace, so a second pipeline from the same
// template needs its own keys (starter columns are per pipeline and never clash).
export function schemaWithSuffix(sections, suffix) {
  if (!suffix) return sections;
  return sections.map((section) => ({ ...section, fields: section.fields.map((item) => (item.starter ? item : { ...item, key: `${item.key}_${suffix}` })) }));
}

// The import payload for a template: rows keyed by the COLUMNS headers.
export function templatePayload(template, { name, description, isDefault = false, keySuffix = '' } = {}) {
  const rows = [];
  template.stages.forEach((stage, index) => {
    const base = { Stage: stage.name, 'Stage order': String(index + 1), Probability: String(stage.probability), Closed: stage.closed ? 'true' : 'false' };
    (stage.steps.length ? stage.steps : ['']).forEach((step) => rows.push({ ...base, Step: step, 'Step type': step ? 'manual' : '' }));
  });
  return {
    name: (name || template.name).trim(),
    description: (description ?? template.description) || null,
    is_default: Boolean(isDefault),
    table_schema: { version: 1, sections: schemaWithSuffix(template.sections, keySuffix) },
    mapping: COLUMNS,
    rows,
  };
}
